/**
 * Reusable validation functions for form fields
 */

export const validators = {
  required: (value, fieldName = 'This field') => {
    if (!value || (typeof value === 'string' && !value?.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },

  // Comprehensive email validation with regex
  email: (value, fieldName = 'Email') => {
    if (!value) return null;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex?.test(value?.trim())) {
      return `${fieldName} must be a valid email address (e.g., user@example.com)`;
    }
    return null;
  },

  // Email required + format validation
  emailRequired: (value, fieldName = 'Email') => {
    if (!value || value?.trim() === '') return `${fieldName} is required`;
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex?.test(value?.trim())) {
      return `${fieldName} must be a valid email address (e.g., user@example.com)`;
    }
    return null;
  },

  minLength: (value, min, fieldName = 'This field') => {
    if (!value) return null;
    if (value?.length < min) {
      return `${fieldName} must be at least ${min} characters`;
    }
    return null;
  },

  maxLength: (value, max, fieldName = 'This field') => {
    if (!value) return null;
    if (value?.length > max) {
      return `${fieldName} must be less than ${max} characters`;
    }
    return null;
  },

  // Amount: required, no negatives, must be > 0
  amount: (value, fieldName = 'Amount') => {
    if (value === null || value === undefined || value === '') return `${fieldName} is required`;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    if (numValue < 0) {
      return `${fieldName} cannot be a negative value`;
    }
    if (numValue === 0) {
      return `${fieldName} must be greater than $0.00`;
    }
    return null;
  },

  // Non-negative: allows 0 but not negatives
  nonNegative: (value, fieldName = 'This field') => {
    if (value === null || value === undefined || value === '') return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    if (numValue < 0) {
      return `${fieldName} cannot be a negative value`;
    }
    return null;
  },

  positiveNumber: (value, fieldName = 'This field') => {
    if (!value) return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      return `${fieldName} cannot be a negative number`;
    }
    return null;
  },

  date: (value, fieldName = 'Date') => {
    if (!value) return `${fieldName} is required`;
    const date = new Date(value);
    if (isNaN(date?.getTime())) {
      return `Please enter a valid ${fieldName?.toLowerCase()}`;
    }
    return null;
  },

  // Prevents future dates (for transaction dates, payment history, receipt dates)
  noFutureDate: (value, fieldName = 'Date') => {
    if (!value) return null;
    const selectedDate = new Date(value);
    const today = new Date();
    today?.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      return `${fieldName} cannot be a future date`;
    }
    return null;
  },

  // Legacy alias kept for backward compatibility
  futureDate: (value, fieldName = 'Date') => {
    if (!value) return null;
    const selectedDate = new Date(value);
    const today = new Date();
    today?.setHours(23, 59, 59, 999);
    if (selectedDate > today) {
      return `${fieldName} cannot be a future date`;
    }
    return null;
  },

  // Requires future date (for bill due dates, goal target dates, retirement dates)
  mustBeFutureDate: (value, fieldName = 'Date') => {
    if (!value) return null;
    const selectedDate = new Date(value);
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      return `${fieldName} must be a future date`;
    }
    return null;
  },

  pastDate: (value, fieldName = 'Date') => {
    if (!value) return null;
    const selectedDate = new Date(value);
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      return `${fieldName} cannot be in the past`;
    }
    return null;
  },

  phone: (value) => {
    if (!value) return null;
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    if (!phoneRegex?.test(value) || value?.replace(/\D/g, '')?.length < 10) {
      return 'Please enter a valid phone number';
    }
    return null;
  },

  postalCode: (value) => {
    if (!value) return null;
    const postalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    if (!postalRegex?.test(value)) {
      return 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
    }
    return null;
  },

  percentage: (value, fieldName = 'Percentage') => {
    if (!value) return null;
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return `${fieldName} must be between 0 and 100`;
    }
    return null;
  },

  age: (value, min = 0, max = 120) => {
    if (!value) return 'Age is required';
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < min || numValue > max) {
      return `Age must be between ${min} and ${max}`;
    }
    return null;
  }
};

/**
 * Combine multiple validators for a single field
 * @param {Array} validatorFns - Array of validator functions
 * @returns {Function} Combined validator function
 */
export const combineValidators = (...validatorFns) => {
  return (value) => {
    for (const validator of validatorFns) {
      const error = validator(value);
      if (error) return error;
    }
    return null;
  };
};

/**
 * Format currency input
 */
export const formatCurrency = (value) => {
  if (!value) return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;
  return numValue?.toFixed(2);
};

/**
 * Format date to YYYY-MM-DD
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d?.getTime())) return '';
  return d?.toISOString()?.split('T')?.[0];
};

/**
 * Get field error state for inline validation display
 * @param {boolean} touched - Whether field has been touched
 * @param {string} error - Error message if validation failed
 * @returns {Object} Error state with border class and message
 */
export const getFieldErrorState = (touched, error) => {
  if (touched && error) {
    return {
      hasError: true,
      borderClass: 'border-red-500 focus:border-red-500 focus:ring-red-500',
      errorMessage: error
    };
  }
  return {
    hasError: false,
    borderClass: '',
    errorMessage: null
  };
};

/**
 * Get field success state for validation display
 * @param {boolean} touched - Whether field has been touched
 * @param {string} error - Error message if validation failed
 * @param {any} value - Current field value
 * @returns {boolean} Whether field is valid and should show success state
 */
export const isFieldValid = (touched, error, value) => {
  return touched && !error && value;
};

export default validators;